import i18next from 'i18next';
import window from 'global/window';
import moment from 'moment';
import cookies from 'js-cookie';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { List } from 'immutable';
import { getItem } from '~helpers/storage';
import { getLocale } from '~helpers/locale';
import { cookieNames } from '~client/constants';
import formatPrice from '~client/helpers/currency';
import { bookingJourneyType } from 'common/constants/booking';
import {
  INSTANT_BOOK_HEADER,
  LEAD_GENERATION_HEADER,
  SEMI_AUTOMATIC_HEADER,
} from '~constants/headers';

/**
 * Temporary: Default From date
 * Unnecessary when from date picker changed in the future
 */
export const getfromDates = () => {
  const month = new Date().getMonth();
  const year = new Date().getFullYear();

  const results = [
    {
      id: `${year - 1}-12`,
      name: `${i18next.t('landlord.dashboard.selectors.date_selector.month.12')} ${year - 1}`,
    },
    {
      id: `${year - 1}-11`,
      name: `${i18next.t('landlord.dashboard.selectors.date_selector.month.11')} ${year - 1}`,
    },
  ];

  for (let i = 1; i <= month + 1; i++) {
    results.unshift({
      id: `${year}-${`0${i}`.slice(-2)}`,
      name: `${i18next.t(
        `landlord.dashboard.selectors.date_selector.month.${`0${i}`.slice(-2)}`,
      )} ${year}`,
    });
  }

  return results;
};

export const getCurrentYearMonth = () => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${`0${currentDate.getMonth() + 1}`.slice(-2)}`;
};

export const isIEBrowser = () => {
  const ua = window.navigator.userAgent;
  return ua.indexOf('MSIE ') !== -1 || ua.indexOf('Trident/') !== -1 || ua.indexOf('Edge/') !== -1;
};

/**
 * Format params with single structure and remove all undefined data
 *
 * @param Object paramsList
 * @param Object data
 * @returns {{}}
 */
export const formatSimpleParams = (defaultParams, data, excludeParams = []) => {
  const formatted = {};
  Object.keys(defaultParams).map(item => {
    if (excludeParams.indexOf(item) !== -1) {
      formatted[item] = data[item] || defaultParams[item];
      return true;
    }

    if (
      typeof data[item] === 'undefined' ||
      data[item] === null ||
      (data[item] && data[item].length === 0)
    ) {
      // Handle if item is empty
      if (defaultParams[item] !== null) {
        formatted[item] = defaultParams[item];
      }
    } else {
      formatted[item] = data[item];
    }
    return true;
  });

  return formatted;
};

export const windowBeforeLoadFunction = e => {
  const confirmationMessage = 'Do you really want to leave?';
  e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+\
  return confirmationMessage;
};

export const getTenancyLength = booking => {
  if (!booking.moveInDate || !booking.moveOutDate) {
    return 0;
  }

  const cycle = booking.property.billingCycle === 'WEEKLY' ? 'weeks' : 'months';
  const tenancyValue = moment(booking.moveOutDate).diff(moment(booking.moveInDate), cycle);

  return `${tenancyValue} ${cycle}`;
};

const defaultSingleDetailPayload = {
  property: {
    billingCycle: '',
    name: '',
    currency: '',
  },
  student: {
    nameAlt: '',
    firstName: '',
    lastName: '',
    firstNameAlt: '',
    lastNameAlt: '',
    nationality: '',
    email: '',
  },
  moveInDate: '',
  moveOutDate: '',
  landlordApplicationId: '',
  id: 0,
  referenceId: '',
  value: '',
  currency: '',
  commission: {
    value: '',
    calculationValue: '',
    calculationType: '',
  },
  createdAt: '',
  completedAt: '',
  status: '',
  landlordBookingStatus: '',
  unitType: {
    name: '',
  },
};

export const showPassportName = (student = {}) => {
  const { passportFirstName, passportMiddleName, passportLastName, firstName, lastName } = student;
  let studentNameArr = [];

  if (passportFirstName || passportLastName || passportMiddleName) {
    studentNameArr = [passportFirstName, passportMiddleName, passportLastName];
  } else {
    studentNameArr = [firstName, lastName];
  }

  studentNameArr = studentNameArr.filter(name => name);

  return studentNameArr.join(' ');
};

const calCommissionValue = bookingItem => {
  if (!bookingItem.commission) {
    return '';
  }

  if (bookingItem.commission.calculationType === 'percentage') {
    return `${bookingItem.commission.calculationValue}%`;
  }

  return formatPrice(bookingItem.commission.calculationValue, bookingItem.commission.currency);
};

const calBonusCommissionValue = bookingItem => {
  if (!bookingItem.commission) {
    return '';
  }

  if (bookingItem.commission.bonusCalculationType === 'percentage') {
    return `${bookingItem.commission.bonusCalculationValue}%`;
  }

  return formatPrice(bookingItem.commission.bonusCalculationValue, bookingItem.currency);
};

export const generateExportData = payload => {
  const headers = {
    name: 'Name',
    alternateName: 'Alternate name',
    landlordAppId: 'Partner App ID',
    bookingId: 'Booking ID',
    email: 'Email',
    studentNationality: 'Student Nationality',
    property: 'Property',
    dayOfCheckInDate: 'Day of Check In Date',
    dayOfCheckOutDate: 'Day of Check Out Date',
    tenancyLength: 'Tenancy Length',
    roomName: 'Room Name',
    applicationCreated: 'Application Created',
    applicationCompleted: 'Application Completed',
    applicationCurrency: 'Application Currency',
    applicationFinalPrice: 'Application Final Price',
    commissionValue: 'Commission Value',
    commissionBonusValue: 'Commission Bonus Value',
    commissionTotal: 'Commission Total',
    status: 'Status',
  };

  const data = [];

  payload.map(item => {
    const mergedItem = Object.assign({}, defaultSingleDetailPayload, item.node);

    data.push({
      name: showPassportName(mergedItem.student),
      alternateName: mergedItem.student.nameAlt
        ? mergedItem.student.nameAlt
        : `${mergedItem.student.firstNameAlt || ''} ${mergedItem.student.lastNameAlt || ''}`,
      landlordAppId: mergedItem.landlordApplicationId,
      bookingId: mergedItem.referenceId,
      email: mergedItem.student.emailAddress,
      studentNationality: mergedItem.student.nationality,
      property: mergedItem.property.name,
      dayOfCheckInDate: mergedItem.moveInDate,
      dayOfCheckOutDate: mergedItem.moveOutDate,
      tenancyLength: getTenancyLength(mergedItem),
      roomName: mergedItem.unitType && mergedItem.unitType.name ? mergedItem.unitType.name : '',
      applicationCreated: mergedItem.createdAt ? moment.utc(mergedItem.createdAt).format('L') : '-',
      applicationCompleted: mergedItem.completedAt
        ? moment.utc(mergedItem.completedAt).format('L')
        : '-',
      applicationCurrency: mergedItem.currency,
      applicationFinalPrice: formatPrice(mergedItem.price, mergedItem.currency),
      commissionValue: calCommissionValue(mergedItem),
      commissionBonusValue: calBonusCommissionValue(mergedItem),
      commissionTotal: mergedItem.commission
        ? formatPrice(mergedItem.commission.total, mergedItem.currency)
        : '',
      status: mergedItem.landlordBookingStatus,
    });

    return true;
  });

  return { headers, data };
};

const httpsRedirect = () => {
  // if (getEnvironment() === environments.DEV) {
  //   return;
  // }
  // const location = window.location;
  // if (location.protocol !== 'https:') {
  //   location.href = location.href.replace(location.protocol, 'https:');
  // }
};

export default httpsRedirect;

export const generateSlug = name =>
  name
    .match(/[a-zA-Z]|[0-9]|\s/g)
    .reduce((result, item) => result + item)
    .replace(/\s/g, '-')
    .toLowerCase();

export const cloneObject = obj => JSON.parse(JSON.stringify(obj));

export const htmlToString = element => {
  if (!element || !element.tagName) {
    return '';
  }
  let text;
  let node = document.createElement('div');
  node.appendChild(element.cloneNode(false));
  text = node.innerHTML;
  const string = text.indexOf('>') + 1;
  text = text.substring(0, string) + element.innerHTML + text.substring(string);
  node = null;
  return text;
};

export const renameObjectKey = (object, oldKey, newKey) => {
  if (oldKey !== newKey && Object.prototype.hasOwnProperty.call(object, oldKey)) {
    Object.defineProperty(object, newKey, Object.getOwnPropertyDescriptor(object, oldKey));
    delete object[oldKey]; // eslint-disable-line no-param-reassign
  }
  return object;
};

export const mergeWithoutList = (a, b) => {
  const isList = List.isList;
  if (a && a.mergeWith && !isList(a) && !isList(b)) {
    return a.mergeWith(mergeWithoutList, b);
  }
  return b;
};

export const getHeader = () => {
  const authorization = `Bearer ${cookies.get(cookieNames.token)}`;
  const headers = {
    Authorization: authorization,
    'Accept-Language': 'en-us',
  };
  const authPayload = getItem('PMS_CURRENT_USER_AUTH');

  if (authPayload && authPayload.payload && authPayload.payload.currentRoleSlug) {
    headers['Current-Role'] = authPayload.payload.currentRoleSlug;
  }

  return headers;
};

export const camelCaseToUpperFirstCase = string => {
  if (typeof string === 'string') {
    // Add ' ' to upper charater
    const newString = string.replace(/([a-z])([A-Z])/g, '$1 $2');
    return newString.charAt(0).toUpperCase() + newString.slice(1);
  }
  return string;
};

export const chooseMomentLocale = () => {
  const locale = getLocale().replace('_', '-');
  if (moment.locales().includes(locale)) {
    return locale;
  } else if (moment.locales().includes(locale.substring(0, locale.indexOf('-')))) {
    // check if the first two letters of the locale
    // are included in the array returned by `locales()`
    // which (in this case) tells us which locales moment will support
    // will fixes crashes caused by "en-US" not being found,
    // as we'll tell moment to load "en" instead
    return locale.substring(0, locale.indexOf('-'));
  }
  return 'en';
};

export const formatFullTime = time => {
  const momentTime = moment(time).locale(chooseMomentLocale());
  return `${momentTime.format('LL')} ${momentTime.format('H:mm:ss')}`;
};

export const orderExportData = (payload, bookingJourney) => {
  let headers = {};

  if (bookingJourney === bookingJourneyType.SEMI_AUTOMATIC) {
    headers = SEMI_AUTOMATIC_HEADER;
  } else if (bookingJourney === bookingJourneyType.LEAD_GENERATION) {
    headers = LEAD_GENERATION_HEADER;
  } else if (bookingJourney === bookingJourneyType.INSTANT_BOOK) {
    headers = INSTANT_BOOK_HEADER;
  }

  const exportOrders = [];

  payload.forEach(order => {
    const exportOrder = {
      referenceId: get(order, 'order.referenceId') || '-',
      studentName: showPassportName(get(order, 'order.student')) || '-',
      studentEmail: get(order, 'order.student.emailAddress') || '-',
      city: get(order, 'order.city.name') || '-',
      propertyName: get(order, 'order.propertyName') || '-',
      moveInDate: get(order, 'order.moveInDate')
        ? moment(get(order, 'order.moveInDate')).format('YYYY-MM-DD')
        : '-',
      moveOutDate: get(order, 'order.moveOutDate')
        ? moment(get(order, 'order.moveOutDate')).format('YYYY-MM-DD')
        : '-',
      createdDate: get(order, 'order.createdAt')
        ? moment(get(order, 'order.createdAt')).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      completedDate: get(order, 'order.completedAt')
        ? moment(get(order, 'order.completedAt')).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      roomName: get(order, 'order.unitTypeName') || '-',
      unitPrice: get(order, 'order.listingMinPrice') || '-',
      totalRent: formatPrice(get(order, 'order.totalRental'), get(order, 'order.currency')) || '-',
      commissionTotal:
        formatPrice(get(order, 'commission.totalPrice'), get(order, 'commission.currency')) || '-',
      commissionTierValue: get(order, 'commission.commissionTierValue') || '-',
      status: order?.status
        ? i18next.t(`landlord.reconciliation_list.table.status.${toLower(get(order, 'status'))}`)
        : '-',
      invoiceNumber: get(order, 'invoiceNumber') || '-',
      invoicedAt: get(order, 'invoicedAt')
        ? moment(get(order, 'invoicedAt')).format('YYYY-MM-DD')
        : '-',
    };

    if (bookingJourney === bookingJourneyType.LEAD_GENERATION) {
      delete exportOrder.completedDate;
    }

    if (bookingJourney === bookingJourneyType.INSTANT_BOOK) {
      delete exportOrder.createdDate;
      delete exportOrder.completedDate;
    }

    exportOrders.push(exportOrder);
  });

  return { headers, data: exportOrders };
};

export const bookingExportData = (payload = []) => {
  const headers = {
    completedAt: 'Booking complete at',
    xbookingReferenceId: 'S.com booking ID',
    landlordApplicationId: 'Partner booking ID',
    studentName: 'Student name',
    studentEmail: 'Student email',
    propertyName: 'Property name',
    roomName: 'Room name',
    billingCycle: 'Billing cycle',
    finalPrice: 'Final price',
    normalCommissionValue: 'Normal Commission value',
    bonusCommissionValue: 'Bonus commission value',
    commissionTotal: 'Commission total',
    repeatType: 'Repeat type',
    moveInDate: 'Move in date',
    moveOutDate: ' Move out date',
    bookingCompleteDate: 'Booking complete date',
    landlordBookingStatus: 'Landlord booking status',
  };

  const exportOpportunities = [];

  payload.forEach(opportunityItem => {
    const opportunity = opportunityItem;

    exportOpportunities.push({
      completedAt: get(opportunity, 'completedAt')
        ? moment(get(opportunity, 'completedAt')).format('YYYY-MM-DD')
        : '-',
      xbookingReferenceId: get(opportunity, 'completedXbooking.referenceId') || '-',
      landlordApplicationId: get(opportunity, 'landlordApplicationId') || '-',
      studentName: showPassportName(get(opportunity, 'student')) || '-',
      studentEmail: get(opportunity, 'student.emailAddress') || '-',
      propertyName: get(opportunity, 'completedXbooking.propertyName') || '-',
      roomName: get(opportunity, 'completedXbooking.roomName') || '-',
      billingCycle: get(opportunity, 'completedXbooking.billingCycle')
        ? i18next.t(
            `landlord.billing_cycle.${toLower(get(opportunity, 'completedXbooking.billingCycle'))}`,
          )
        : '-',
      finalPrice:
        formatPrice(
          get(opportunity, 'finalPrice'),
          get(opportunity, 'completedXbooking.currency'),
        ) || '-',
      normalCommissionValue: get(opportunity, 'completedXbooking.commission.value') || '-',
      bonusCommissionValue: get(opportunity, 'completedXbooking.commission.bonusValue') || '-',
      commissionTotal:
        formatPrice(
          get(opportunity, 'completedXbooking.commission.totalCommissionValue'),
          get(opportunity, 'completedXbooking.commission.currency'),
        ) || '-',
      repeatType: get(opportunity, 'repeatType') || '-',
      moveInDate: get(opportunity, 'finalMoveInDate')
        ? moment(get(opportunity, 'finalMoveInDate')).format('YYYY-MM-DD')
        : '-',
      moveOutDate: get(opportunity, 'finalMoveOutDate')
        ? moment(get(opportunity, 'finalMoveOutDate')).format('YYYY-MM-DD')
        : '-',
      bookingCompleteDate: get(opportunity, 'completedAt')
        ? moment(get(opportunity, 'completedAt')).format('YYYY-MM-DD')
        : '-',
      landlordBookingStatus: get(opportunity, 'landlordBookingStatus')
        ? i18next.t(
            `landlord.reconciliation_list.table.status.${toLower(
              get(opportunity, 'landlordBookingStatus'),
            )}`,
          )
        : '-',
    });
  });

  return { headers, data: exportOpportunities };
};
